import ArrowRight2Icon from '../../../../../../assets/svgs/ArrowRight2'
import BookmarkIcon from '../../../../../../assets/svgs/Bookmark'
import LiveIcon from '../../../../../../assets/svgs/Live'
import NotesIcon from '../../../../../../assets/svgs/Notes'
import PenIcon from '../../../../../../assets/svgs/Pen'
import PeopleIcon from '../../../../../../assets/svgs/People'
import RingIcon from '../../../../../../assets/svgs/Ring'

import {
  brandAlpha,
  compactNumber,
  formatShortDate,
  joinParts,
  readableOn,
  shade,
  truncate,
} from '../../../format'

import style from './claimed.module.css'

/**
 * A claimed booth on the fair floor.
 *
 * Deliberately a different object from the scraped card beside it. The standard
 * three-column card is right for a company we only observed; it has nothing to
 * say about one that has a voice.
 *
 * The brand colour is confined to the banner and the accents rather than
 * flooding the whole card — a full-bleed fill drowned the content and made a
 * grid of booths shout at each other. A coloured banner over a light body is
 * also closer to what a stand actually looks like.
 */

/**
 * The status row: what is true about this booth right now, as icon + number.
 * Only live facts, capped at three — a row of four is a table, not a glance.
 */
const buildStatus = (company, sprint) => {
  const counts = company.counts || {}
  const out = []

  if (sprint && sprint.registrationOpen) {
    out.push({ key: 'open', Icon: null, dot: true, label: 'Registration open' })
  } else if (company.isLive) {
    out.push({ key: 'live', Icon: null, dot: true, label: company.liveLabel || 'Active now' })
  }

  if (sprint && sprint.roles.length) {
    out.push({
      key: 'roles',
      Icon: PenIcon,
      label: `${sprint.roles.length} ${sprint.roles.length === 1 ? 'role' : 'roles'}`,
    })
  } else if (counts.openJobs > 0) {
    out.push({ key: 'jobs', Icon: PenIcon, label: `${compactNumber(counts.openJobs)} open roles` })
  }

  if (sprint && sprint.spotsLeft !== null && sprint.spotsLeft !== undefined) {
    out.push({ key: 'places', Icon: PeopleIcon, label: `${sprint.spotsLeft} places left` })
  } else if (counts.events > 0) {
    out.push({ key: 'events', Icon: RingIcon, label: `${compactNumber(counts.events)} events` })
  } else if (counts.news > 0) {
    out.push({ key: 'news', Icon: NotesIcon, label: `${compactNumber(counts.news)} updates` })
  }

  return out.slice(0, 3)
}

/**
 * What the `?` beside the staffed line says.
 *
 * Short on purpose — a popover on a card is read at a glance, and the previous
 * three-sentence version explained the feature instead of selling the offer.
 * The seniority comes from the role on the answers themselves, so this stays
 * true when the person staffing a booth is a recruiter rather than a founder.
 */
const hostTipFor = (host) => {
  const who = host.role ? host.role.toLowerCase() : 'person who answers here'
  const pace = host.typicalReply ? ` Replies ${host.typicalReply}.` : ''

  return `Talk to the ${who}. Not an agent — a human, for a change.${pace}`
}

const ClaimedBooth = ({ company, onOpen, onToggleSave }) => {
  const brand = company.brandColor || '#432C5B'
  const ink = readableOn(brand)
  const sprint = company.sprint || null

  const meta = joinParts([company.industry, company.location], ' · ')
  const status = buildStatus(company, sprint)
  const host = company.host || null
  const badge = company.badge || null
  const hostTip = host ? hostTipFor(host) : null

  const sprintFacts = sprint
    ? joinParts([
        sprint.spotsLeft !== null ? `${sprint.spotsLeft} places left` : null,
        formatShortDate(sprint.startsAt),
      ])
    : null

  const handleSave = (event) => {
    event.stopPropagation()
    onToggleSave(company)
  }

  return (
    <div className={style.shell}>
      <button
        type="button"
        className={style.booth}
        onClick={() => onOpen(company)}
        style={{ '--brand': brand, '--ink': ink }}
        data-log-action="spotlight_open_company"
      >
        {/* The banner. Colour lives here and in the accents — not across the
            whole card. */}
        <div
          className={style.banner}
          style={{
            background: `linear-gradient(135deg, ${brand} 0%, ${shade(brand, 0.28)} 100%)`,
            color: ink,
          }}
        >
          <span
            className={style.sign}
            style={{
              background: brandAlpha(ink, 0.16),
              boxShadow: `inset 0 0 0 1.5px ${brandAlpha(ink, 0.34)}`,
            }}
            aria-hidden="true"
          >
            {company.initials}
          </span>

          <span className={style.ident}>
            <span className={style.name}>{company.name}</span>
            {meta && <span className={style.meta}>{meta}</span>}
          </span>
        </div>

        <div className={style.body}>
          {company.tagline && (
            <p className={style.tagline}>{truncate(company.tagline, 120)}</p>
          )}

          {/*
            One row for the two things that are true about this stand right now:
            who is standing at it, and what it has been awarded. Stacked, they
            cost a line the card does not have; on the banner, the badge collided
            with the industry line. Both tooltips anchor to this row rather than
            to their own pill, so a 230px popover cannot spill past the card edge
            and get clipped by the booth's overflow.
          */}
          {(host || badge) && (
            <div className={style.signals}>
              {/*
                The counts that used to sit here just repeated the pass below.
                This says the one thing no other booth on the floor can: a named
                human is standing at it, and here is how fast they answer.
                Derived from questions actually answered, so it is evidence
                rather than a "we're responsive!" badge anyone could set.

                CSS-driven tooltip rather than a nested button: this whole card
                is already a <button>, so a real one inside it would be invalid
                HTML. The text is also on aria-label, so it is not hover-only.
              */}
              {host && (
                <p className={style.staffed} aria-label={hostTip}>
                  <span className={style.staffed_dot} aria-hidden="true" />
                  <span className={style.staffed_text}>
                    <strong>{host.firstName}</strong> is on this stand
                  </span>
                  <span className={style.staffed_hint} aria-hidden="true">?</span>

                  <span className={style.tip} role="tooltip">
                    {hostTip}
                  </span>
                </p>
              )}

              {/* Awarded on a countable measure, never set by the company. */}
              {badge && (
                <span className={style.badge} aria-label={badge.detail || badge.label}>
                  {badge.label}
                  <span className={style.badge_hint} aria-hidden="true">?</span>
                  <span className={style.tip} role="tooltip">
                    {badge.detail || badge.label}
                  </span>
                </span>
              )}
            </div>
          )}

          {!host && status.length > 0 && (
            <ul className={style.status}>
              {status.map(({ key, Icon, dot, label }) => (
                <li className={style.status_item} key={key}>
                  {dot ? (
                    <span className={style.status_dot} aria-hidden="true" />
                  ) : (
                    Icon && (
                      <span className={style.status_icon} aria-hidden="true">
                        <Icon />
                      </span>
                    )
                  )}
                  {label}
                </li>
              ))}
            </ul>
          )}

          <div className={style.pass}>
            <span className={`${style.notch} ${style.notch_l}`} aria-hidden="true" />
            <span className={`${style.notch} ${style.notch_r}`} aria-hidden="true" />

            <span className={style.pass_left}>
              {sprint ? (
                <>
                  <span className={style.pass_flag} style={{ color: brand }}>
                    <LiveIcon />
                    48h sprint
                  </span>
                  {sprintFacts && <span className={style.pass_facts}>{sprintFacts}</span>}
                </>
              ) : (
                <span className={style.pass_facts}>Company profile</span>
              )}
            </span>

            <span className={style.pass_cta} style={{ background: brand, color: ink }}>
              {sprint ? 'Register' : 'Visit'}
              <ArrowRight2Icon />
            </span>
          </div>
        </div>
      </button>

      <button
        type="button"
        className={company.saved ? `${style.save} ${style.save_on}` : style.save}
        onClick={handleSave}
        aria-label={company.saved ? `Remove ${company.name} from saved` : `Save ${company.name}`}
        aria-pressed={Boolean(company.saved)}
        style={{ background: brandAlpha(ink, 0.18), color: ink }}
        data-log-action="spotlight_toggle_save"
      >
        <BookmarkIcon />
      </button>
    </div>
  )
}

export default ClaimedBooth
